import {
  Box,
  Flex,
  InputGroup,
  InputLeftElement,
  Text
} from '@chakra-ui/react';
import { FormFeedback, Input } from '@morressier/alembic';
import { useContext, useEffect } from 'react';
import {
  Controller,
  useFormContext,
  FieldError as IFieldError,
  get
} from 'react-hook-form';
import { IPInfoContext } from 'ip-info-react';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import { IFormFieldText } from 'src/components/Submission/GenericFieldComponents/Text';
import { FieldLabel } from 'src/components/Field';
import SelectCountryCode, {
  CountryCodeSelection,
  getCountryData
} from './SelectCountruWIthFlag';

export type PhoneFieldData = {
  country?: string;
  countryCallingCode: string;
  value?: string;
  fullNumber?: string;
};

const MAX = 15;
const MIN = 3;

const sanitizeDigits = (value?: string): string =>
  value?.replace(/\D/g, '') || '';

const normalizeCountryCallingCode = (value?: string): string => {
  const digits = sanitizeDigits(value);
  return digits;
};

const getCountryFromCallingCode = (
  countryCallingCode?: string,
  ipCountry?: string
) => {
  const normalizedCountryCallingCode = sanitizeDigits(countryCallingCode);
  if (!normalizedCountryCallingCode) return undefined;
  const matches = getCountries().filter(
    country => getCountryCallingCode(country) === normalizedCountryCallingCode
  );
  if (matches.length === 0) return undefined;
  if (matches.length === 1) return matches[0];
  // Shared calling code (e.g., +1 for US/CA): disambiguate via IP country, else bail.
  return ipCountry
    ? matches.find(c => c === ipCountry.toUpperCase())
    : undefined;
};

const validatePhone = (nationalDigits: string, required: boolean) => {
  if (!nationalDigits) return required ? 'Phone number is required' : true;

  if (nationalDigits.length < MIN) {
    return `Phone number must be minimum ${MIN} digits`;
  }
  if (nationalDigits.length > MAX) {
    return `Phone number must be maximum ${MAX} digits`;
  }

  return true;
};

const PhoneNumberInput = ({ config }: { config: IFormFieldText }) => {
  const {
    formState: { errors },
    control,
    setValue,
    watch
  } = useFormContext();

  const {
    name,
    params: { validations, labels },
    caseDataFields: { main: field }
  } = config;

  // Watch the phone object
  const phoneData = watch(field) as PhoneFieldData | undefined;
  const userIPInfo = useContext(IPInfoContext);

  const error = get(errors, field) as IFieldError;

  useEffect(() => {
    if (phoneData?.country) return;

    const hasCallingCode = !!sanitizeDigits(phoneData?.countryCallingCode);
    const derivedCountry = getCountryFromCallingCode(
      phoneData?.countryCallingCode,
      userIPInfo?.country_code
    );
    // Only fall back to IP-derived country when no calling code is set,
    // otherwise we'd pair a country with a mismatched calling code.
    const defaultCountryData =
      !hasCallingCode && userIPInfo?.country_code
        ? getCountryData(userIPInfo.country_code)
        : undefined;

    const nextCountry = derivedCountry ?? defaultCountryData?.country;
    if (!nextCountry) return;

    setValue(
      field,
      {
        ...phoneData,
        country: nextCountry,
        countryCallingCode:
          phoneData?.countryCallingCode ||
          normalizeCountryCallingCode(`+${getCountryCallingCode(nextCountry)}`)
      },
      { shouldValidate: false, shouldDirty: false, shouldTouch: false }
    );
  }, [phoneData, userIPInfo?.country_code]);

  const handlePhoneChange = (inputValue: string) => {
    const nationalDigits = sanitizeDigits(inputValue);
    const normalizedCountryCallingCode = normalizeCountryCallingCode(
      phoneData?.countryCallingCode
    );
    const normalizedFullNumber =
      normalizedCountryCallingCode && nationalDigits
        ? `${normalizedCountryCallingCode}${nationalDigits}`
        : undefined;

    const updatedPhoneData: PhoneFieldData = {
      ...phoneData,
      countryCallingCode: normalizedCountryCallingCode,
      value: nationalDigits,
      fullNumber: normalizedFullNumber
    };

    setValue(field, updatedPhoneData, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
  };

  const handleCountryChange = (country: CountryCodeSelection | undefined) => {
    const nationalDigits = sanitizeDigits(phoneData?.value);
    const normalizedCountryCallingCode = normalizeCountryCallingCode(
      country?.countryCode
    );
    const normalizedFullNumber =
      normalizedCountryCallingCode && nationalDigits
        ? `${normalizedCountryCallingCode}${nationalDigits}`
        : undefined;

    const updatedData: PhoneFieldData = {
      ...phoneData,
      country: country?.country,
      countryCallingCode: normalizedCountryCallingCode,
      value: nationalDigits,
      fullNumber: normalizedFullNumber
    };

    setValue(field, updatedData, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
  };

  const dynamicPL =
    phoneData?.countryCallingCode?.length &&
    phoneData?.countryCallingCode?.length > 1
      ? 40
      : 34;

  return (
    <Flex direction={'column'} key={name} gap={2}>
      <FieldLabel
        label={labels.main}
        subText={labels.subtitle}
        isRequired={validations.required}
        helpText={labels.helpText}
      />
      <Controller
        name={field}
        control={control}
        rules={{
          validate: (data: PhoneFieldData | undefined) => {
            return validatePhone(
              sanitizeDigits(data?.value),
              validations.required
            );
          }
        }}
        render={({
          field: { ...field },
          fieldState: { error: controllerError }
        }) => (
          <Box>
            <Flex
              border="1px solid"
              alignItems={'center'}
              borderColor="inputBorder"
              rounded="md"
            >
              <SelectCountryCode
                value={phoneData?.country ?? undefined}
                placeholder="Select country code"
                onChange={handleCountryChange}
              />
              <InputGroup
                display="flex"
                alignItems="baseline"
                justifyContent={'center'}
                h={'40px'}
              >
                <InputLeftElement pointerEvents="none" height="inherit">
                  <Text>+{phoneData?.countryCallingCode}</Text>
                </InputLeftElement>

                <Input
                  {...field}
                  name={field.name}
                  value={phoneData?.value || ''}
                  onChange={e => handlePhoneChange(e.target.value)}
                  pl={`${dynamicPL}px`}
                  border="none"
                  height="inherit"
                  aria-label={field.name}
                />
              </InputGroup>
            </Flex>

            {(controllerError || error)?.message && (
              <FormFeedback variant="error">
                {controllerError?.message || error?.message}
              </FormFeedback>
            )}
          </Box>
        )}
      />
    </Flex>
  );
};

export default PhoneNumberInput;
